import {
  HttpResponse,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { throwError } from "rxjs";
import { catchError, finalize, tap, share } from "rxjs/operators";

import { AuthService } from "@services/auth.service";
import { LoadingService } from "@services/loading.service";
import { ToastService } from "@services/toast.service";
import { formatApiErrorMessage } from "@shared/utils/api-response.utils";

import type {
  HttpInterceptorFn,
  HttpErrorResponse
} from "@angular/common/http";

/**
 * Modern Functional Interceptor for handling Auth, Loader, and Global Error states.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const loader = inject(LoadingService);
  const toastService = inject(ToastService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Helper to determine if we are on the login page
  const isLoginPage = () => router.url === "/pages/login";

  // Add Auth Header if user is logged in
  const currentUser = localStorage.getItem("currentUser");
  let authReq = req;
  if (currentUser) {
    authReq = req.clone({
      headers: req.headers.set("Authorization", "Bearer " + currentUser),
    });
  }

  // Set default JSON Accept header
  authReq = authReq.clone({
    headers: authReq.headers.set("Accept", "application/json"),
  });

  // Handle skipLoader parameter
  let skipLoader = false;
  if (authReq.params && authReq.params.has("skipLoader")) {
    skipLoader = authReq.params.get("skipLoader")?.toLowerCase() === "true";
    const newParams = authReq.params.delete("skipLoader");
    authReq = authReq.clone({ params: newParams });
  }

  if (!skipLoader) loader.show();

  return next(authReq).pipe(
    share(),
    tap((event) => {
      if (event instanceof HttpResponse) {
        const body = event.body as {
          success?: boolean;
          message?: string;
          errors?: string[] | Record<string, string[] | string>;
        } | null;
        if (body) {
          const isSuccess = body.success !== false;
          const isMutative = ["POST", "PUT", "DELETE", "PATCH"].includes(authReq.method);

          if (!isSuccess) {
            toastService.error(
              formatApiErrorMessage(
                body,
                "Si è verificato un errore durante l'operazione",
              ),
            );
          } else if (isMutative && body.message) {
            toastService.success(body.message);
          }
        }
      }
    }),
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        const errorBody = err.error as { Message?: string } | null;
        const formattedMessage = errorBody?.Message?.replace(/,\s*/g, "<br>") ?? "";
        const message = formattedMessage
          ? `Sessione scaduta: ${formattedMessage}`
          : "Sessione scaduta. Per favore, effettua di nuovo il login.";

        if (!isLoginPage()) {
          authService.forceLogout({
            message,
            title: "Auth",
            redirectDelayMs: 3000,
            enableHtml: !!formattedMessage,
          });
        }
      }

      return throwError(() => err);
    }),
    finalize(() => {
      loader.hide();
    })
  );
};
